import { inject, observer } from 'mobx-react';
import type { User as OidcUser } from 'oidc-client-ts';
import React, { useState } from 'react';
import {
  usePostV4ManufacturerByRouteManufacturerIdReportClaimEmailMutation,
  usePostV4ManufacturerByRouteManufacturerIdReportInventoryEmailMutation,
  usePostV4ManufacturerByRouteManufacturerIdReportOpenOrdersEmailMutation,
  usePostV4ManufacturerByRouteManufacturerIdReportProductsEmailMutation,
  usePostV4ManufacturerByRouteManufacturerIdReportPurchasedOrdersEmailMutation,
  usePostV4ManufacturerByRouteManufacturerIdReportReorderEmailMutation,
  usePostV4ManufacturerByRouteManufacturerIdReportSalesEmailMutation,
} from 'randmar-api-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from 'randmarcomps';
import { FileText } from 'lucide-react';
import { RootStore } from '../../stores/rootStore';
import { useManufacturer } from '../../contexts/WithManufacturer';

interface IProps {
  store?: RootStore;
  buttonTitle: string;
  type: string;
  children?: React.ReactNode;
  user?: OidcUser;
  disabled?: boolean;
  triggerClassName?: string;
  triggerVariant?: React.ComponentProps<typeof Button>["variant"];
  triggerAriaLabel?: string;
  triggerDescribedBy?: string;
}

const DownloadReport: React.FC<IProps> = ({
  store,
  buttonTitle,
  type,
  children,
  user,
  disabled = false,
  triggerClassName,
  triggerVariant,
  triggerAriaLabel,
  triggerDescribedBy,
}) => {
  const { applicationId } = useManufacturer();
  // State
  const [open, setOpen] = useState(false);
  const [canGenerate, setCanGenerate] = useState(true);
  const [email, setEmail] = useState(user?.profile.email ?? store?.userStore.getApplicationName() ?? '');
  const [dateStart, setDateStart] = useState<string | undefined>(undefined);
  const [dateEnd, setDateEnd] = useState<string | undefined>(undefined);

  // Hooks
  const { toast } = useToast();
  const [downloadSales] = usePostV4ManufacturerByRouteManufacturerIdReportSalesEmailMutation();
  const [downloadClaim] = usePostV4ManufacturerByRouteManufacturerIdReportClaimEmailMutation();
  const [downloadInventory] = usePostV4ManufacturerByRouteManufacturerIdReportInventoryEmailMutation();
  const [downloadOpenOrders] = usePostV4ManufacturerByRouteManufacturerIdReportOpenOrdersEmailMutation();
  const [downloadProducts] = usePostV4ManufacturerByRouteManufacturerIdReportProductsEmailMutation();
  const [downloadPurchasedOrders] = usePostV4ManufacturerByRouteManufacturerIdReportPurchasedOrdersEmailMutation();
  const [downloadReorder] = usePostV4ManufacturerByRouteManufacturerIdReportReorderEmailMutation();

  const handleButtonClick = () => {
    if (disabled || !canGenerate) {
      return;
    }

    setOpen(true);
  };

  const handleConfirm = async () => {
    setOpen(false);
    setCanGenerate(false);

    try {
      if (store) {
        // Legacy MobX Path
        const { manufacturerStore, snackStore } = store;
        let task;
        switch (type) {
          case "Sales":
            task = manufacturerStore.downloadSales(applicationId, email, dateStart || "", dateEnd || "");
            break;
          case "Claim":
            task = manufacturerStore.downloadClaim(applicationId, email, dateStart || "", dateEnd || "");
            break;
          case "Inventory":
            task = manufacturerStore.downloadInventory(applicationId, email, dateStart || "");
            break;
          default:
            task = manufacturerStore.downloadReport(applicationId, type, email);
            break;
        }

        snackStore.showWarning(`File is being generated`);
        const success = await task;

        if (success) {
          snackStore.showSuccess(`File will be sent to ${email}.`);
        } else {
          snackStore.showError(`Error sending file to ${email}.`);
        }
      } else {
        // New RTK Path
        toast({
          title: "Generating Report",
          description: `File is being generated and will be sent to ${email}`,
        });

        let promise;
        const args = { routeManufacturerId: applicationId, emailAddress: email };

        switch (type) {
          case "Sales":
            promise = downloadSales({ ...args, startDate: dateStart, endDate: dateEnd }).unwrap();
            break;
          case "Claim":
            promise = downloadClaim({ ...args, startDate: dateStart, endDate: dateEnd }).unwrap();
            break;
          case "Inventory":
            promise = downloadInventory({ ...args, inventoryDate: dateStart }).unwrap();
            break;
          case "OpenOrders":
            promise = downloadOpenOrders(args).unwrap();
            break;
          case "Products":
            promise = downloadProducts(args).unwrap();
            break;
          case "PurchasedOrders":
            promise = downloadPurchasedOrders(args).unwrap();
            break;
          case "Reorder":
            promise = downloadReorder(args).unwrap();
            break;
          default:
            throw new Error(`Unsupported report type: ${type}`);
        }

        await promise;
        toast({
          title: "Success",
          description: `File will be sent to ${email}.`,
        });
      }
    } catch {
      if (!store) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `An error occurred while generating the report.`,
        });
      }
    } finally {
      setCanGenerate(true);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleEmailChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const emailRegExp: RegExp = new RegExp("[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}");

  return (
    <>
      <Button
        type="button"
        title={buttonTitle}
        variant={triggerVariant}
        onClick={handleButtonClick}
        className={`rounded-full data-[busy=true]:animate-pulse ${triggerClassName ?? ""}`}
        disabled={disabled || !canGenerate}
        aria-label={triggerAriaLabel ?? buttonTitle}
        aria-describedby={triggerDescribedBy}
        aria-busy={!canGenerate}
        data-busy={!canGenerate}
      >
        <span className="flex items-center gap-2">
          {(children || <FileText className="h-4 w-4" aria-hidden="true" />)}
          <span aria-live="polite">{!canGenerate ? "Preparing..." : buttonTitle}</span>
        </span>
      </Button>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download {buttonTitle}</DialogTitle>
            <DialogDescription>
              Please enter your email address, so we can send you the file once generated.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                autoFocus
                value={email}
                onChange={handleEmailChanged}
                className="col-span-3"
              />
            </div>
            {(type === "Sales" || type === "Inventory" || type === "Claim") && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="dateStart" className="text-right">
                  Start Date
                </Label>
                <Input
                  id="dateStart"
                  type="date"
                  onChange={(e) => setDateStart(e.target.value)}
                  className="col-span-3"
                />
              </div>
            )}
            {(type === "Sales" || type === "Claim") && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="dateEnd" className="text-right">
                  End Date
                </Label>
                <Input
                  id="dateEnd"
                  type="date"
                  onChange={(e) => setDateEnd(e.target.value)}
                  className="col-span-3"
                />
              </div>
            )}
          </div>
          <p className="pt-4 text-sm text-muted-foreground">It will take about 5 minute to generate.</p>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!emailRegExp.test(email) || !canGenerate || (type === "Sales" ? (!dateStart || !dateEnd || dateStart === "" || dateEnd === "") : false)}
            >
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default inject('store')(observer(DownloadReport));
